/**
 * Composition root. Builds the simulation, attaches presentation subsystems,
 * and starts the frame loop.
 *
 * Thin by design: it composes and owns nothing. Anything that looks like game
 * logic here belongs in a subsystem.
 */

import { Sim, type MatchDefinition, type SimOptions } from './sim/index.js'
import { Renderer } from './render/index.js'
import { Build } from './sim/build.js'
import { Selection } from './input/select.js'
import { Orders } from './input/orders.js'
import { CameraControl } from './input/camera.js'
import { InputController } from './input/index.js'
import { ControlGroups } from './input/keys.js'
import { PlacementMode } from './input/placement.js'
import { Sidebar } from './ui/sidebar.js'
import { NetStatus } from './ui/netstatus.js'
import { Hints } from './ui/hints.js'
import { Outcome } from './ui/outcome.js'
import { Audio } from './audio/index.js'
import { defaultConfig } from './core/config.js'
import { startClock, type Clock } from './core/clock.js'
import './ui/styles.css'

export interface BootOptions {
  mount: HTMLElement
  match: MatchDefinition
  localSlot?: number
  /** Skip the requestAnimationFrame loop; the capture harness drives manually. */
  manualFrames?: boolean
  quantize?: boolean
  /** Wall-clock speed multiplier. 1 is real time; 3 is three times as fast. */
  speed?: number
  /** The CRT display filter. Defaults on; the capture harness turns it off. */
  /** Omit the DOM input bindings. The capture harness has no user. */
  noInput?: boolean
  /** Omit the sidebar and HUD. Used for world-only shots. */
  noHud?: boolean
  /** Omit audio. The capture harness has no speakers and no user gesture. */
  noAudio?: boolean
  /**
   * Use an already-connected relay instead of the local one. The lobby owns the
   * socket — it has to, since it is talking to the relay before the match
   * exists — so the match is built around that connection rather than opening
   * a second one.
   */
  relay?: SimOptions['relay']
}

export interface Game {
  readonly sim: Sim
  /** Network banner and F3 panel. Null when the HUD is off. */
  readonly netStatus: NetStatus | null
  readonly renderer: Renderer
  readonly selection: Selection
  readonly sidebar: Sidebar | null
  /** Advance the simulation and render one frame. Used by the capture harness. */
  frame(dtMs: number): void
  /** Run the simulation to a tick, then render once. Deterministic. */
  seekTo(tick: number): void
  /** Change the wall-clock speed multiplier. */
  setSpeed(multiplier: number): void
  readonly speed: number
  stop(): void
}

export function boot(opts: BootOptions): Game {
  const { mount } = opts

  const canvas = document.createElement('canvas')
  canvas.id = 'world'
  mount.appendChild(canvas)

  const hud = document.createElement('div')
  hud.id = 'hud'
  // The overlay never swallows a click; only its interactive children do.
  hud.style.cssText = 'position:absolute;inset:0;pointer-events:none'
  mount.appendChild(hud)

  /**
   * Wall-clock to simulation-time multiplier.
   *
   * Pacing only: it changes how quickly real seconds are turned into ticks, not
   * what a tick does. So it cannot desync a match — two clients on different
   * settings simply run at the slower one's pace, because the tick barrier
   * commits a tick only once every player has submitted for it.
   */
  let speed = opts.speed ?? 1

  const config = defaultConfig()
  config.quantize = opts.quantize ?? false

  const renderer = new Renderer(canvas, 'blue-alert', !opts.noHud)
  const selection = new Selection()
  const orders = new Orders()
  const cameraControl = new CameraControl()
  const groups = new ControlGroups()
  const placement = new PlacementMode(hud)
  const sidebar = opts.noHud ? null : new Sidebar(hud)
  const netStatus = opts.noHud ? null : new NetStatus(hud)
  const hints = opts.noHud ? null : new Hints(hud)
  const outcome = opts.noHud ? null : new Outcome(hud)
  const audio = opts.noAudio ? null : new Audio()
  const input = opts.noInput ? null : new InputController(mount)

  const sim = Sim.create({
    match: opts.match,
    localSlot: opts.localSlot ?? 0,
    config,
    ...(opts.relay ? { relay: opts.relay } : {}),
    // Presentation subsystems attach before init, so they are initialized in
    // dependency order alongside the simulation rather than bolted on after.
    attach: (registry) => {
      registry.add(Renderer, renderer)
      registry.add(Selection, selection)
      if (hints) registry.add(Hints, hints)
      if (outcome) registry.add(Outcome, outcome)
      registry.add(Orders, orders)
      registry.add(CameraControl, cameraControl)
      registry.add(ControlGroups, groups)
      registry.add(PlacementMode, placement)
      if (sidebar) registry.add(Sidebar, sidebar)
      if (audio) registry.add(Audio, audio)
      if (input) registry.add(InputController, input)
    },
  })

  // The only write path out of input is the relay. Orders holds a reference to
  // it and nothing else; it cannot touch the simulation directly even by
  // accident.
  const localSlot = opts.localSlot ?? 0
  orders.relay = sim.relay
  placement.relay = sim.relay
  selection.localSlot = localSlot
  if (hints) hints.localSlot = localSlot
  if (outcome) outcome.localSlot = opts.noHud ? -1 : localSlot
  placement.localSlot = localSlot
  renderer.setMarkerSource(orders)
  renderer.setLocalSlot(opts.noHud ? -1 : localSlot)
  if (input) input.localSlot = localSlot

  if (audio) {
    audio.localSlot = localSlot
    // Browsers require a user gesture before audio may start.
    const unlock = () => {
      audio.resume()
      window.removeEventListener('pointerdown', unlock)
      window.removeEventListener('keydown', unlock)
    }
    window.addEventListener('pointerdown', unlock)
    window.addEventListener('keydown', unlock)
  }

  if (sidebar) {
    sidebar.localSlot = localSlot
    sidebar.callbacks = {
      onQueue: (tab, kind) => {
        sim.relay.submit({ t: 7, tab, kind } as never)
      },
      onCancel: (tab, index) => {
        sim.relay.submit({ t: 8, tab, index } as never)
      },
      onRepair: () => orders.issueRepair(),
      onSell: () => orders.issueSell(),
      onDeploy: () => orders.issueDeploy(),
      onPlaceRequest: (kind) => placement.enterPlacement(kind),
      onSuperweaponRequest: () => placement.enterSuperTarget(),
      onMinimapClick: (cx, cy) => renderer.centerOn(cx, cy),
      onToggleMusic: () => toggleMusic(),
      onCycleSpeed: () => cycleSpeed(),
    }
    sidebar.setMusicEnabled(audio?.music.enabled ?? false)
    sidebar.setSpeed(speed)
  }

  /**
   * Put the most recently finished structure on the cursor.
   *
   * A structure that has finished building is not placed yet, and the only cue
   * is its sidebar icon changing — which a new player does not know to look
   * for. This is the one-key version of noticing.
   */
  function quickPlace(): void {
    const kind = sim.registry.get<Build>('build').readyKindAt(localSlot, 0)
    if (kind > 0) placement.enterPlacement(kind)
  }


  function toggleMusic(): void {
    if (!audio) return
    audio.music.setEnabled(!audio.music.enabled)
    sidebar?.setMusicEnabled(audio.music.enabled)
  }

  if (input) {
    input.hooks = {
      onTab: (tab) => sidebar?.setTab(tab),
      onCenter: (x, z) => renderer.centerOn(x, z),
      onToggleMusic: () => toggleMusic(),
      onToggleNetPanel: () => netStatus?.toggle(),
      onCycleSpeed: () => cycleSpeed(),
      onQuickPlace: () => quickPlace(),
    }
  }

  /**
   * Cycle the speed. A short ladder rather than a slider: the point is to skip
   * the slow part of a friendly match, not to tune anything.
   */
  function cycleSpeed(): void {
    const ladder = [1, 2, 3, 4]
    speed = ladder[(ladder.indexOf(speed) + 1) % ladder.length] ?? 1
    sidebar?.setSpeed(speed)
  }

  // Open on the local player's base, not the map centre — a first frame
  // showing empty terrain reads as a broken build.
  const slot = opts.localSlot ?? 0
  const sx = sim.world.starts[slot * 2]
  const sy = sim.world.starts[slot * 2 + 1]
  if (sx >= 0) renderer.centerOn(sx, sy)

  const onResize = () => renderer.resize(window.innerWidth, window.innerHeight)
  window.addEventListener('resize', onResize)

  let clock: Clock | null = null

  function frame(dtMs: number, visible = true): void {
    // The listener follows the camera: the player's ear is where their eye is.
    if (audio) {
      const target = renderer.cameraTarget()
      audio.setListener(target.x, target.z)
    }
    // `engine.frame` drives the fixed steps (which capture transforms through
    // the renderer's postFixedUpdate) and then the render-rate update pass.
    //
    // While hidden, the simulation still steps — a lockstep peer is waiting on
    // this client's input — but there is no point drawing to a tab nobody is
    // looking at.
    sim.engine.frame(dtMs * speed, { render: visible, maxCatchUp: Math.ceil(8 * speed) })
    if (visible) {
      netStatus?.update()
      if (hints) {
        hints.placing = placement.active
        hints.update()
      }
    }
  }

  function seekTo(tick: number): void {
    while (sim.tick < tick) if (!sim.step()) break
    sim.registry.update(0, 0)
  }

  if (!opts.manualFrames) clock = startClock((dt, visible) => frame(dt, visible))

  return {
    sim,
    netStatus,
    get speed() {
      return speed
    },
    setSpeed(multiplier: number) {
      speed = Math.max(0.25, Math.min(8, multiplier))
      sidebar?.setSpeed(speed)
    },
    renderer,
    selection,
    sidebar,
    frame,
    seekTo,
    stop() {
      clock?.stop()
      clock = null
      window.removeEventListener('resize', onResize)
      sim.dispose()
    },
  }
}
